/**
 * ResponseBadge — Unit 6 of the listing-conversion overhaul plan.
 *
 * Live "En línea ahora · Respondemos en <30 min" badge for the
 * `<SpecialistCard />`. Driven by `useBusinessHours()` — derives state
 * client-side from `America/Mexico_City` time (Mon-Sat 09:00-20:00 MX =
 * online). During business hours the dot pulses green; outside, the dot
 * dims to grey and the copy switches to a delayed response message.
 *
 * Pulse animation uses Tailwind's `animate-ping` on a stacked dot, which
 * stays on compositor-friendly properties (`transform`, `opacity`) per
 * the project's animation rule.
 *
 * Locale keys are populated by Unit 20 in `public/locales/{es,en}/details.json`:
 *   - `RESPONSE_BADGE.ONLINE`  → "En línea ahora · Respondemos en <30 min"
 *   - `RESPONSE_BADGE.DELAYED` → "Respondemos al siguiente día hábil"
 *
 * SSR-safe by self-gating: `useBusinessHours()` reads `new Date()`, so the
 * server clock (UTC→MX on Vercel) and the client clock can resolve to
 * different online/delayed states across a business-hours boundary —
 * rendering green on SSR and grey on CSR (or vice versa) → React 19 #418.
 * Confirmed firing as a green→grey className drift on listing pages +
 * /flotillas (Datadog 2026-06-09, the dominant real trigger once the
 * recorder's inline-style false positives were normalized away). The
 * `mounted` gate below renders NOTHING on the server and on the first
 * client render (identical → no mismatch), then reveals the real badge
 * after hydration. This makes the component correct regardless of how it's
 * consumed — the old "consumers must dynamic(ssr:false)" contract was
 * fragile and SpecialistCard rendered it directly, which is what fired.
 */

import { useTranslation } from 'next-i18next'
import { useEffect, useState } from 'react'

import { useBusinessHours } from 'src/components/LeadForm/useBusinessHours'

export default function ResponseBadge() {
  const { t } = useTranslation('details')
  const { online, visible } = useBusinessHours()
  // Client-only mount gate — see the SSR-safety note in the file header.
  // Server + first client render both return null (no hydration mismatch);
  // the badge pops in post-hydration. It's a small inline status chip, so
  // the deferred reveal has no layout-shift cost.
  const [mounted, setMounted] = useState(false)
  useEffect(() => setMounted(true), [])

  // Quiet hours (23:00-04:59 MX). Hide the badge entirely rather than
  // substituting a delayed-response string the specialist can't honor
  // overnight. Also hidden pre-mount (SSR + first paint) for hydration safety.
  if (!mounted || !visible) return null

  return (
    <div
      aria-live="polite"
      className={
        online
          ? 'inline-flex items-center gap-2 border border-green-500/30 bg-green-100 px-2.5 py-1 text-[11px] font-medium tracking-[0.04em] text-ink-700'
          : 'inline-flex items-center gap-2 border border-grey-300 bg-grey-100 px-2.5 py-1 text-[11px] font-medium tracking-[0.04em] text-ink-200'
      }
    >
      <span aria-hidden="true" className="relative inline-flex size-2">
        {online && (
          <span className="absolute inline-flex size-full animate-ping rounded-full bg-green-500 opacity-75" />
        )}
        <span
          className={
            online
              ? 'relative inline-flex size-2 rounded-full bg-green-500'
              : 'relative inline-flex size-2 rounded-full bg-grey-500'
          }
        />
      </span>
      {/* Short copy on mobile (avoids wrapping to two lines inside the
          SpecialistCard's tight inline layout), full copy on desktop where
          the extra "responde en <30 min" / "in a few hours" detail fits. */}
      <span className="md:hidden">
        {online
          ? t('RESPONSE_BADGE.ONLINE_SHORT')
          : t('RESPONSE_BADGE.DELAYED_SHORT')}
      </span>
      <span className="hidden md:inline">
        {online ? t('RESPONSE_BADGE.ONLINE') : t('RESPONSE_BADGE.DELAYED')}
      </span>
    </div>
  )
}
